import { createTheme } from '@mui/material/styles';

// InTouch Theme: Warm, friendly design for a social connections app
export const colorSchemes = {
  intouch: {
    name: 'InTouch',
    primary: {
      main: '#4F7A65',      // Deep sage: calm, growth, connection
      light: '#6F9C84',
      dark: '#426653',
      contrastText: '#FFFDF9'
    },
    secondary: {
      main: '#D9825F',      // Muted coral: human warmth
      light: '#E7A07E',
      dark: '#B9684C',
      contrastText: '#171514'
    },
  }
};

const paletteTokens = {
  light: {
    background: '#FAF7F2',
    paper: '#FFFDF9',
    elevated: '#F3EEE7',
    textPrimary: '#2F2A26',
    textSecondary: '#756B62',
    primary: '#4F7A65',
    primaryLight: '#6F9C84',
    primaryDark: '#426653',
    primarySoft: '#DDEBE3',
    secondary: '#D9825F',
    secondaryLight: '#E7A07E',
    secondaryDark: '#B9684C',
    secondarySoft: '#F7E1D5',
    accent: '#A894C8',
    warning: '#D4A24C',
    error: '#C96A6A',
    success: '#5E8C76',
    info: '#6D94B8',
    divider: '#E6DED4',
    fieldBorder: '#D8CEC2',
    icon: '#756B62',
    scrollbar: '#CFC4B8',
    relationship: {
      family: { main: '#D9825F', soft: '#F7E1D5', border: '#E8B99F', text: '#7B3F2E' },
      friend: { main: '#A894C8', soft: '#EAE3F5', border: '#D2C4E8', text: '#5E4A7A' },
      professional: { main: '#6D94B8', soft: '#E0EBF4', border: '#B8D0E5', text: '#355C7B' },
      support: { main: '#4F7A65', soft: '#DDEBE3', border: '#B7D2C3', text: '#2F604B' },
      other: { main: '#9E8A78', soft: '#EFE7DE', border: '#D8C7B6', text: '#655345' },
    },
  },
  dark: {
    background: '#171514',
    paper: '#211D1A',
    elevated: '#2A2420',
    textPrimary: '#F1ECE6',
    textSecondary: '#B9AEA5',
    primary: '#8DB79E',
    primaryLight: '#A8CFB8',
    primaryDark: '#6F9C84',
    primarySoft: '#26382F',
    secondary: '#E3A077',
    secondaryLight: '#F0B892',
    secondaryDark: '#BE7F5B',
    secondarySoft: '#3A2821',
    accent: '#B8A3D6',
    warning: '#D8B15F',
    error: '#D78383',
    success: '#8DB79E',
    info: '#8DB7D7',
    divider: '#3A332E',
    fieldBorder: '#4A4039',
    icon: '#B9AEA5',
    scrollbar: '#5A5048',
    relationship: {
      family: { main: '#F0A37B', soft: '#3A2821', border: '#76503E', text: '#FFD9C2' },
      friend: { main: '#B8A3D6', soft: '#30283A', border: '#5F4C77', text: '#E3D7F5' },
      professional: { main: '#8DB7D7', soft: '#22313D', border: '#48667D', text: '#D6ECFA' },
      support: { main: '#8DB79E', soft: '#26382F', border: '#4F725D', text: '#DDF2E5' },
      other: { main: '#C4AB93', soft: '#322A24', border: '#66564A', text: '#EAD8C6' },
    },
  },
};

const getTheme = (mode, colorScheme = 'intouch') => {
  const isLight = mode === 'light';
  const tokens = isLight ? paletteTokens.light : paletteTokens.dark;
  const colors = {
    ...colorSchemes.intouch,
    primary: {
      main: tokens.primary,
      light: tokens.primaryLight,
      dark: tokens.primaryDark,
      contrastText: isLight ? '#FFFDF9' : '#171514',
    },
    secondary: {
      main: tokens.secondary,
      light: tokens.secondaryLight,
      dark: tokens.secondaryDark,
      contrastText: '#171514',
    },
  };

  return createTheme({
    spacing: 6.67,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    palette: {
      mode,
      primary: {
        main: colors.primary.main,
        light: colors.primary.light,
        dark: colors.primary.dark,
        contrastText: colors.primary.contrastText,
      },
      secondary: {
        main: colors.secondary.main,
        light: colors.secondary.light,
        dark: colors.secondary.dark,
        contrastText: colors.secondary.contrastText,
      },
      background: {
        default: tokens.background,
        paper: tokens.paper,
      },
      text: {
        primary: tokens.textPrimary,
        secondary: tokens.textSecondary,
      },
      error: {
        main: tokens.error,
      },
      info: {
        main: tokens.info,
      },
      warning: {
        main: tokens.warning,
      },
      success: {
        main: tokens.success,
      },
      divider: tokens.divider,
      action: {
        active: tokens.primary,
        hover: isLight ? 'rgba(94, 140, 118, 0.07)' : 'rgba(141, 183, 158, 0.10)',
        selected: isLight ? 'rgba(94, 140, 118, 0.12)' : 'rgba(141, 183, 158, 0.16)',
        disabled: isLight ? 'rgba(0, 0, 0, 0.26)' : 'rgba(255, 255, 255, 0.3)',
        disabledBackground: isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
      },
      customGradients: {
        header: tokens.paper,
        card: tokens.paper,
        primary: tokens.primary,
        secondary: tokens.secondary,
      },
      relationship: tokens.relationship,
      isGlassmorphism: false,
    },
    typography: {
      fontFamily: [
        'Inter',
        '-apple-system',
        'BlinkMacSystemFont',
        'Segoe UI',
        'Roboto',
        'Helvetica Neue',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: {
        fontSize: '2rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        color: tokens.textPrimary,
        lineHeight: 1.3,
      },
      h2: {
        fontSize: '1.67rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        color: tokens.textPrimary,
        lineHeight: 1.35,
      },
      h3: {
        fontSize: '1.33rem',
        fontWeight: 600,
        letterSpacing: '-0.005em',
        color: tokens.textPrimary,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: '1.17rem',
        fontWeight: 600,
        color: tokens.textPrimary,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1rem',
        fontWeight: 500,
        color: tokens.textPrimary,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: '0.9rem',
        fontWeight: 500,
        color: tokens.textPrimary,
        lineHeight: 1.5,
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
        letterSpacing: '0.01em',
        fontSize: '0.875rem',
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
      },
      subtitle1: {
        fontSize: '1.125rem',
        lineHeight: 1.5,
        fontWeight: 500,
      },
      subtitle2: {
        fontSize: '1rem',
        lineHeight: 1.5,
        fontWeight: 500,
      },
      caption: {
        fontSize: '0.8125rem',
        lineHeight: 1.5,
        fontWeight: 400,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            fontWeight: 500,
            fontSize: '0.875rem',
            padding: '8px 20px',
            minHeight: '38px',
            transition: 'all 0.2s ease',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: isLight
                ? '0 2px 8px rgba(94, 140, 118, 0.22)'
                : '0 2px 8px rgba(141, 183, 158, 0.24)',
              transform: 'translateY(-1px)',
            },
          },
          contained: {
            backgroundColor: colors.primary.main,
            color: colors.primary.contrastText,
            '&:hover': {
              backgroundColor: colors.primary.dark,
            },
          },
          outlined: {
            borderColor: tokens.fieldBorder,
            color: tokens.textPrimary,
            '&:hover': {
              borderColor: colors.primary.main,
              backgroundColor: isLight ? 'rgba(94, 140, 118, 0.07)' : 'rgba(141, 183, 158, 0.12)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.paper,
            backgroundImage: 'none',
            boxShadow: isLight
              ? '0 1px 3px rgba(0, 0, 0, 0.06)'
              : '0 1px 3px rgba(0, 0, 0, 0.2)',
            border: `1px solid ${tokens.divider}`,
            borderRadius: '12px',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.paper,
            backgroundImage: 'none',
            border: `1px solid ${tokens.divider}`,
            borderRadius: '12px',
            boxShadow: isLight
              ? '0 1px 3px rgba(0, 0, 0, 0.06)'
              : '0 1px 3px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: isLight
                ? '0 4px 12px rgba(0, 0, 0, 0.08)'
                : '0 4px 12px rgba(0, 0, 0, 0.3)',
              transform: 'translateY(-2px)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              backgroundColor: isLight ? tokens.paper : tokens.background,
              '& fieldset': {
                borderColor: tokens.fieldBorder,
                borderWidth: '1px',
              },
              '&:hover fieldset': {
                borderColor: colors.primary.light,
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.primary.main,
                borderWidth: '2px',
              },
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem',
              '&.Mui-focused': {
                color: colors.primary.main,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            borderRadius: '8px',
            border: `1px solid ${tokens.divider}`,
            backgroundColor: isLight ? tokens.elevated : 'rgba(255, 255, 255, 0.05)',
            color: tokens.textSecondary,
            fontWeight: 500,
            fontSize: '0.8125rem',
            transition: 'all 0.2s ease',
            ...(ownerState.color === 'primary' && {
              borderColor: isLight ? tokens.primaryLight : tokens.primaryDark,
              backgroundColor: isLight ? tokens.primarySoft : tokens.primarySoft,
              color: isLight ? tokens.primaryDark : tokens.relationship.support.text,
            }),
            ...(ownerState.color === 'secondary' && {
              borderColor: isLight ? tokens.secondaryLight : tokens.secondaryDark,
              backgroundColor: isLight ? tokens.secondarySoft : tokens.secondarySoft,
              color: isLight ? tokens.secondaryDark : tokens.relationship.family.text,
            }),
            ...(ownerState.color === 'success' && {
              borderColor: tokens.relationship.support.border,
              backgroundColor: tokens.relationship.support.soft,
              color: tokens.relationship.support.text,
            }),
            ...(ownerState.color === 'warning' && {
              borderColor: isLight ? '#E5C17A' : '#78603A',
              backgroundColor: isLight ? '#F8EBCB' : '#3D321F',
              color: isLight ? '#74521B' : '#F7DE9A',
            }),
            ...(ownerState.color === 'error' && {
              borderColor: isLight ? '#E6B0B0' : '#7A4545',
              backgroundColor: isLight ? '#F8DEDE' : '#3B2323',
              color: isLight ? '#843E3E' : '#F3C4C4',
            }),
            '&:hover': {
              backgroundColor: isLight ? colors.primary.main : colors.primary.main,
              color: colors.primary.contrastText,
              borderColor: colors.primary.main,
            },
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: tokens.paper,
            backgroundImage: 'none',
            border: `1px solid ${tokens.fieldBorder}`,
            borderRadius: '16px',
            boxShadow: isLight
              ? '0 20px 40px rgba(0, 0, 0, 0.1)'
              : '0 20px 40px rgba(0, 0, 0, 0.4)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.paper,
            backgroundImage: 'none',
            borderBottom: `1px solid ${tokens.divider}`,
            boxShadow: isLight
              ? '0 1px 2px rgba(0, 0, 0, 0.04)'
              : '0 1px 2px rgba(0, 0, 0, 0.2)',
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            backgroundColor: colors.primary.main,
            color: colors.primary.contrastText,
            borderRadius: '14px',
            boxShadow: isLight
              ? '0 4px 12px rgba(94, 140, 118, 0.28)'
              : '0 4px 12px rgba(141, 183, 158, 0.28)',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: colors.primary.dark,
              transform: 'translateY(-2px)',
              boxShadow: isLight
                ? '0 6px 16px rgba(94, 140, 118, 0.32)'
                : '0 6px 16px rgba(141, 183, 158, 0.32)',
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: tokens.background,
            color: tokens.textPrimary,
          },
          '::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '::-webkit-scrollbar-track': {
            background: tokens.background,
          },
          '::-webkit-scrollbar-thumb': {
            background: tokens.scrollbar,
            borderRadius: '4px',
            '&:hover': {
              background: isLight ? tokens.textSecondary : '#756A61',
            },
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: tokens.icon,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: tokens.icon,
            padding: '8px',
            borderRadius: '10px',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: isLight ? 'rgba(94, 140, 118, 0.08)' : 'rgba(141, 183, 158, 0.12)',
              color: colors.primary.main,
            },
          },
        },
      },
    },
  });
};

export default getTheme;
